import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatDate } from '../../utils/formatters.js';
import { leaveTypeColor, leaveTypeLabel } from '../../utils/leaveTypes.js';

/**
 * Abwesenheitsarten und Feiertage pflegen.
 *
 * Zwei Dinge, die vorher nur ein Entwickler ändern konnte.
 *
 * Die Arten: createLeaveTypeSchema und LeaveTypeRepository.create waren
 * fertig gebaut und nur nie angebunden. Es gab also einen Weg, eine Art
 * anzulegen - er führte bloss nirgendwohin.
 *
 * Die Feiertage: die Tabelle war bis 2031 gefüllt. Ab dem 1. Januar 2032
 * hätte jeder Feiertag wieder einen Ferientag gekostet, und Brückentage und
 * Betriebsferien standen ohnehin nie drin - die kann kein Seed kennen, weil
 * sie je Firma anders sind.
 */

const FARBEN = ['neutral', 'info', 'success', 'warning', 'danger', 'accent'];
const ARTEN = ['PUBLIC', 'BRIDGE', 'COMPANY'];

const LEERE_ART = {
  code: '',
  name: '',
  isPaid: true,
  defaultAnnualDays: '0',
  requiresBalance: false,
  requiresApproval: true,
  allowsRetroactive: true,
  requiresDocument: false,
  countsAsWorktime: true,
  minNoticeDays: '0',
  colorToken: 'neutral',
  active: true,
};

const heute = () => new Date().toISOString().slice(0, 10);

function Schalter({ label, checked, onChange, hint }) {
  return (
    <label className="pf-role-option">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint && <span className="muted text-xs"> {hint}</span>}
      </span>
    </label>
  );
}

function ArtenTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [offen, setOffen] = useState(false);
  const [form, setForm] = useState(LEERE_ART);
  const [fehler, setFehler] = useState(null);

  const arten = useQuery({
    queryKey: ['hr', 'leaveTypes', 'all'],
    queryFn: () => hrApi.listAllLeaveTypes().then((r) => r.items || []),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['hr', 'leaveTypes'] });

  const speichern = useMutation({
    mutationFn: () => {
      const rumpf = {
        name: form.name.trim(),
        isPaid: form.isPaid,
        defaultAnnualDays: Number(form.defaultAnnualDays || 0),
        requiresBalance: form.requiresBalance,
        requiresApproval: form.requiresApproval,
        allowsRetroactive: form.allowsRetroactive,
        requiresDocument: form.requiresDocument,
        countsAsWorktime: form.countsAsWorktime,
        minNoticeDays: Number(form.minNoticeDays || 0),
        colorToken: form.colorToken,
        active: form.active,
      };
      /* Der CODE ist nur beim Anlegen dabei. An ihm hängen Guthabenzeilen,
         Anträge und die Übersetzungsschlüssel - ihn nachträglich zu ändern
         verwaist alles drei auf einmal. */
      return form.id
        ? hrApi.updateLeaveType(form.id, rumpf)
        : hrApi.createLeaveType({ ...rumpf, code: form.code.trim().toUpperCase() });
    },
    onSuccess: () => {
      setFehler(null);
      setOffen(false);
      refresh();
    },
    onError: (err) => setFehler(err?.message || t('common.loadError')),
  });

  const entfernen = useMutation({
    mutationFn: (id) => hrApi.deleteLeaveType(id),
    onSuccess: () => {
      setFehler(null);
      refresh();
    },
    onError: (err) => setFehler(err?.message || t('common.loadError')),
  });

  function bearbeiten(art) {
    setForm({
      ...art,
      defaultAnnualDays: String(art.defaultAnnualDays ?? 0),
      minNoticeDays: String(art.minNoticeDays ?? 0),
    });
    setFehler(null);
    setOffen(true);
  }

  const spalten = [
    {
      key: 'name',
      label: t('hr.types.col.name'),
      render: (r) => (
        <span className="lt-name">
          <span className="lt-dot" style={{ background: leaveTypeColor(r.colorToken) }} />
          {leaveTypeLabel(t, r)}
          {!r.active && <Badge variant="neutral">{t('hr.types.inactive')}</Badge>}
        </span>
      ),
    },
    { key: 'code', label: t('hr.types.col.code'), render: (r) => <span className="mono text-xs">{r.code}</span> },
    {
      key: 'quota',
      label: t('hr.types.col.quota'),
      align: 'right',
      render: (r) => (r.requiresBalance ? <span className="num mono">{Number(r.defaultAnnualDays).toFixed(1)}</span> : '-'),
    },
    {
      key: 'rules',
      label: t('hr.types.col.rules'),
      render: (r) => (
        <span className="lt-flags">
          {!r.requiresApproval && <Badge variant="success">{t('hr.types.flag.noApproval')}</Badge>}
          {!r.allowsRetroactive && <Badge variant="info">{t('hr.types.flag.noRetro')}</Badge>}
          {r.requiresDocument && <Badge variant="warning">{t('hr.types.flag.document')}</Badge>}
          {r.countsAsWorktime === false && (
            <Badge variant="danger">{t('hr.types.flag.consumesTime')}</Badge>
          )}
          {r.minNoticeDays > 0 && (
            <Badge variant="neutral">{t('hr.types.flag.notice', { days: r.minNoticeDays })}</Badge>
          )}
        </span>
      ),
    },
    {
      key: 'do',
      label: '',
      align: 'right',
      render: (r) => (
        <div className="decide-actions">
          <Button size="sm" variant="ghost" onClick={() => bearbeiten(r)}>
            {t('common.edit')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => entfernen.mutate(r.id)}>
            {t('common.delete')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-actions" style={{ marginBottom: 'var(--space-3)' }}>
        <Button
          variant="primary"
          onClick={() => {
            setForm(LEERE_ART);
            setFehler(null);
            setOffen(true);
          }}
        >
          {t('hr.types.new')}
        </Button>
      </div>

      {fehler && <div className="alert danger">{fehler}</div>}

      <Table
        columns={spalten}
        rows={arten.data}
        isLoading={arten.isLoading}
        emptyMessage={t('hr.types.empty')}
      />

      <Modal
        open={offen}
        title={form.id ? t('hr.types.edit') : t('hr.types.new')}
        onClose={() => setOffen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOffen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => speichern.mutate()}
              disabled={speichern.isPending || !form.name.trim() || (!form.id && !form.code.trim())}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="field-row">
          {!form.id && (
            <label className="field">
              <span className="field-label">{t('hr.types.col.code')}</span>
              <input
                className="input mono"
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="HITZEFREI"
              />
              <span className="muted text-xs">{t('hr.types.codeHint')}</span>
            </label>
          )}
          <label className="field">
            <span className="field-label">{t('hr.types.col.name')}</span>
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('hr.types.color')}</span>
            <select
              className="select"
              value={form.colorToken}
              onChange={(e) => setForm((p) => ({ ...p, colorToken: e.target.value }))}
            >
              {FARBEN.map((f) => (
                <option key={f} value={f}>
                  {t(`hr.types.color.${f}`)}
                </option>
              ))}
            </select>
            {/* Ein Tokenname, kein Farbwert - die Gestaltung kommt aus
                tokens.css und kennt beide Farbschemata. */}
            <span className="muted text-xs">{t('hr.types.colorHint')}</span>
          </label>
          <label className="field">
            <span className="field-label">{t('hr.types.notice')}</span>
            <input
              className="input"
              type="number"
              min="0"
              max="365"
              value={form.minNoticeDays}
              onChange={(e) => setForm((p) => ({ ...p, minNoticeDays: e.target.value }))}
            />
          </label>
        </div>

        <Schalter
          label={t('hr.types.requiresBalance')}
          hint={t('hr.types.requiresBalanceHint')}
          checked={form.requiresBalance}
          onChange={(v) => setForm((p) => ({ ...p, requiresBalance: v }))}
        />
        {form.requiresBalance && (
          <label className="field" style={{ maxWidth: '12rem' }}>
            <span className="field-label">{t('hr.types.quota')}</span>
            <input
              className="input"
              type="number"
              min="0"
              step="0.5"
              value={form.defaultAnnualDays}
              onChange={(e) => setForm((p) => ({ ...p, defaultAnnualDays: e.target.value }))}
            />
          </label>
        )}
        <Schalter
          label={t('hr.types.requiresApproval')}
          hint={t('hr.types.requiresApprovalHint')}
          checked={form.requiresApproval}
          onChange={(v) => setForm((p) => ({ ...p, requiresApproval: v }))}
        />
        <Schalter
          label={t('hr.types.allowsRetroactive')}
          hint={t('hr.types.allowsRetroactiveHint')}
          checked={form.allowsRetroactive}
          onChange={(v) => setForm((p) => ({ ...p, allowsRetroactive: v }))}
        />
        <Schalter
          label={t('hr.types.requiresDocument')}
          hint={t('hr.types.requiresDocumentHint')}
          checked={form.requiresDocument}
          onChange={(v) => setForm((p) => ({ ...p, requiresDocument: v }))}
        />
        {/* Der Schalter, an dem das Stundenkonto hängt. Aus heisst: ein Tag
            dieser Art zehrt das Guthaben auf, statt es zu bestätigen. Bei
            allen Arten ausser der Kompensation gehört er an - wer krank ist,
            soll keine Ueberstunden verlieren. */}
        <Schalter
          label={t('hr.types.countsAsWorktime')}
          hint={t('hr.types.countsAsWorktimeHint')}
          checked={form.countsAsWorktime}
          onChange={(v) => setForm((p) => ({ ...p, countsAsWorktime: v }))}
        />
        <Schalter
          label={t('hr.types.isPaid')}
          checked={form.isPaid}
          onChange={(v) => setForm((p) => ({ ...p, isPaid: v }))}
        />
        <Schalter
          label={t('hr.types.active')}
          hint={t('hr.types.activeHint')}
          checked={form.active}
          onChange={(v) => setForm((p) => ({ ...p, active: v }))}
        />

        {fehler && <div className="alert danger">{fehler}</div>}
      </Modal>
    </>
  );
}

function FeiertageTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [offen, setOffen] = useState(false);
  const [form, setForm] = useState({ from: heute(), to: '', name: '', kind: 'COMPANY' });
  const [fehler, setFehler] = useState(null);

  const tage = useQuery({
    queryKey: ['hr', 'holidays', jahr],
    queryFn: () => hrApi.listHolidays({ year: jahr }).then((r) => r.items || []),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['hr', 'holidays'] });

  const anlegen = useMutation({
    mutationFn: () =>
      hrApi.createHoliday({
        from: form.from,
        to: form.to || form.from,
        name: form.name.trim(),
        kind: form.kind,
      }),
    onSuccess: () => {
      setFehler(null);
      setOffen(false);
      setForm({ from: heute(), to: '', name: '', kind: 'COMPANY' });
      refresh();
    },
    onError: (err) => setFehler(err?.message || t('common.loadError')),
  });

  const entfernen = useMutation({
    mutationFn: (id) => hrApi.deleteHoliday(id),
    onSuccess: () => {
      setFehler(null);
      refresh();
    },
    onError: (err) => setFehler(err?.message || t('common.loadError')),
  });

  const spalten = [
    { key: 'date', label: t('hr.holidays.col.date'), render: (r) => formatDate(r.holidayDate) },
    { key: 'name', label: t('hr.holidays.col.name'), render: (r) => r.name },
    {
      key: 'kind',
      label: t('hr.holidays.col.kind'),
      render: (r) => (
        <Badge variant={r.kind === 'PUBLIC' ? 'info' : r.kind === 'BRIDGE' ? 'success' : 'warning'}>
          {t(`hr.holidays.kind.${r.kind}`)}
        </Badge>
      ),
    },
    {
      key: 'do',
      label: '',
      align: 'right',
      render: (r) => (
        <div className="decide-actions">
          <Button size="sm" variant="ghost" onClick={() => entfernen.mutate(r.id)}>
            {t('common.delete')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="week-nav">
        <Button size="sm" variant="ghost" onClick={() => setJahr((j) => j - 1)}>
          {jahr - 1}
        </Button>
        <span className="mono">{jahr}</span>
        <Button size="sm" variant="ghost" onClick={() => setJahr((j) => j + 1)}>
          {jahr + 1}
        </Button>
        <Button variant="primary" size="sm" onClick={() => setOffen(true)} style={{ marginLeft: 'auto' }}>
          {t('hr.holidays.new')}
        </Button>
      </div>

      {fehler && <div className="alert danger">{fehler}</div>}

      {/* Der Grund, warum es diese Seite gibt: ohne sie kostete ab dem
          1. Januar 2032 jeder Feiertag wieder einen Ferientag. */}
      {!tage.isLoading && (tage.data || []).length === 0 && (
        <div className="alert warning">{t('hr.holidays.emptyYear', { year: jahr })}</div>
      )}

      <Table
        columns={spalten}
        rows={tage.data}
        isLoading={tage.isLoading}
        emptyMessage={t('hr.holidays.empty')}
      />

      <Modal
        open={offen}
        title={t('hr.holidays.new')}
        onClose={() => setOffen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOffen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => anlegen.mutate()}
              disabled={anlegen.isPending || !form.name.trim() || !form.from}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {/* Von und bis, nicht ein einzelnes Datum: Betriebsferien sind kein
            Tag, sondern die Woche zwischen Weihnachten und Neujahr. */}
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('hr.holidays.from')}</span>
            <input
              className="input"
              type="date"
              value={form.from}
              onChange={(e) => setForm((p) => ({ ...p, from: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field-label">{t('hr.holidays.to')}</span>
            <input
              className="input"
              type="date"
              min={form.from}
              value={form.to}
              onChange={(e) => setForm((p) => ({ ...p, to: e.target.value }))}
            />
            <span className="muted text-xs">{t('hr.holidays.toHint')}</span>
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('hr.holidays.col.name')}</span>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
        </label>
        <label className="field">
          <span className="field-label">{t('hr.holidays.col.kind')}</span>
          <select
            className="select"
            value={form.kind}
            onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))}
          >
            {ARTEN.map((k) => (
              <option key={k} value={k}>
                {t(`hr.holidays.kind.${k}`)}
              </option>
            ))}
          </select>
        </label>

        {fehler && <div className="alert danger">{fehler}</div>}
      </Modal>
    </>
  );
}

export function LeaveTypesPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('types');

  const tabs = [
    { key: 'types', label: t('hr.types.tab') },
    { key: 'holidays', label: t('hr.holidays.tab') },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('hr.types.title')}</h1>
          <p className="muted">{t('hr.types.subtitle')}</p>
        </div>
      </header>

      <div className="tab-bar">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            className={tab === item.key ? 'active' : undefined}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'types' ? <ArtenTab /> : <FeiertageTab />}
    </section>
  );
}
